import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalDatagrid from '../../components/mui/DataGrid/GlobalDatagrid';

import { SkillxXpAwards_Table_Head } from '../../components/datagrid/skillx/XpAwardsTableHead';
import { getSkillxManualXpAwards, getSkillxXpRules } from '../../redux/features/skillxSlice';
import { getAllBatchNameNoPagination } from '../../redux/features/batchSlice';
import { savePaginationData } from '../../redux/features/globalDatagridSlice';
import AwardXpDialog from './xp/AwardXpDialog';
import RevokeXpDialog from './xp/RevokeXpDialog';
import { SX } from './leaderboard/leaderboardData';

/**
 * Award XP & audit history — the mentor's page.
 *
 * One screen for both halves of manual XP: the button that gives it, and the
 * complete record of everything ever given. They belong together — a mentor
 * about to award should be looking at what has already been awarded, and an
 * auditor should be able to see the same list without a separate report.
 *
 * The list is server-paginated and server-filtered (the ledger grows without
 * bound, unlike the rules table), and defaults to showing revoked awards
 * alongside active ones: a revoked award is the most interesting row in an
 * audit, so hiding it by default would be the wrong way round.
 */

const filterBatchOptions = createFilterOptions({
  limit: 100,
  stringify: (option) => option.course_batch_name || '',
});

/** One header statistic. */
function StatCard({ icon, value, label, color }) {
  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        borderRadius: `${SX.radius.md}px`,
        background: SX.card,
        border: `1px solid ${SX.borderStrong}`,
        boxShadow: SX.shadowSoft,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: `${SX.radius.sm}px`,
            background: `${color}14`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon icon={icon} width={20} height={20} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.1, color: SX.text }}>
            {value}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: SX.textMuted }}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

export default function SkillxXpAwards() {
  const dispatch = useDispatch();
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { xpAwards, loading } = useSelector((state) => state.skillx);
  const { batchesAll } = useSelector((state) => state.batch);
  const { gridSize, pageNum, search } = paginationData;

  // Filters live here rather than in the grid toolbar: they are server
  // parameters, and the grid's own quick-filter only carries free text.
  const [batchId, setBatchId] = useState('');
  const [ruleKey, setRuleKey] = useState('');
  const [status, setStatus] = useState('ALL');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Reason options for the filter. Every rule, not just the manually-awardable
  // ones — a rule can be closed to manual awarding after awards were already
  // made under it, and those rows still have to be findable.
  const [ruleOptions, setRuleOptions] = useState([]);

  const dispatchparams = useMemo(
    () => ({
      page: pageNum,
      limit: gridSize,
      search: search || '',
      batchId,
      ruleKey,
      status,
      fromDate,
      toDate,
    }),
    [pageNum, gridSize, search, batchId, ruleKey, status, fromDate, toDate],
  );

  useEffect(() => {
    dispatch(getAllBatchNameNoPagination({ isAllBatchesNeeded: true }));
    dispatch(getSkillxXpRules({ includeInactive: true }))
      .unwrap()
      .then((res) => setRuleOptions(res?.data || []))
      .catch(() => setRuleOptions([]));
  }, [dispatch]);

  useEffect(() => {
    if (pageNum) dispatch(getSkillxManualXpAwards(dispatchparams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchparams]);

  /**
   * Changing a filter has to send the grid back to page 1 — page 3 of the old
   * result set is usually empty in the new one, which reads as "no awards" when
   * there are plenty. The grid owns pagination in redux, so the reset goes
   * through the same slice it reads from.
   */
  const withPageReset = (setter) => (value) => {
    setter(value);
    if ((pageNum || 1) !== 1) dispatch(savePaginationData({ ...paginationData, pageNum: 1 }));
  };

  const meta = xpAwards?.meta || {};
  const selectedBatch = useMemo(
    () => (batchesAll || []).find((b) => b.course_batch_id === batchId) || null,
    [batchesAll, batchId],
  );

  // Revoke lives in the row itself rather than behind the anchor drawer: the
  // decision to revoke is made while reading the row, so the control belongs
  // beside what it acts on.
  const columns = useMemo(
    () => [
      ...SkillxXpAwards_Table_Head,
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 170,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <RevokeXpDialog rowData={params.row} dispatchparams={dispatchparams} />
          </Stack>
        ),
      },
    ],
    [dispatchparams],
  );

  return (
    <GlobalWrapper
      title="SkillX — Award XP"
      actions={
        <AwardXpDialog
          dispatchparams={dispatchparams}
          onAwarded={(params) => dispatch(getSkillxManualXpAwards(params))}
        />
      }
    >
      {/* ---------- what the filtered set contains ---------- */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:hand-stars-bold-duotone"
            value={(meta.totalRecords || 0).toLocaleString()}
            label="Awards in this view"
            color={SX.blue}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:bolt-bold-duotone"
            value={(meta.activeXp || 0).toLocaleString()}
            label="XP still standing"
            color="#2E7D62"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:users-group-rounded-bold-duotone"
            value={meta.learnerCount || 0}
            label="Learners awarded"
            color={SX.purple}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:undo-left-round-bold-duotone"
            value={meta.revokedCount || 0}
            label="Revoked"
            color="#c0392b"
          />
        </Grid>
      </Grid>

      {/* ---------- server-side filters ---------- */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: `${SX.radius.md}px`,
          background: SX.card,
          border: `1px solid ${SX.borderStrong}`,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={batchesAll || []}
              filterOptions={filterBatchOptions}
              getOptionLabel={(option) => option.course_batch_name || ''}
              isOptionEqualToValue={(option, value) =>
                option.course_batch_id === value?.course_batch_id
              }
              value={selectedBatch}
              onChange={(e, option) => withPageReset(setBatchId)(option?.course_batch_id ?? '')}
              renderInput={(params) => (
                <TextField {...params} label="Batch" autoComplete="off" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              size="small"
              fullWidth
              label="Reason"
              value={ruleKey}
              onChange={(e) => withPageReset(setRuleKey)(e.target.value)}
            >
              <MenuItem value="">All reasons</MenuItem>
              {ruleOptions.map((r) => (
                <MenuItem key={r.rule_key} value={r.rule_key}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              size="small"
              fullWidth
              label="Status"
              value={status}
              onChange={(e) => withPageReset(setStatus)(e.target.value)}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="REVOKED">Revoked</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              size="small"
              fullWidth
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => withPageReset(setFromDate)(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              size="small"
              fullWidth
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => withPageReset(setToDate)(e.target.value)}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ width: '100%', padding: 0 }}>
        <GlobalDatagrid
          tableHead={columns}
          rows={xpAwards?.data || []}
          meta={meta.totalRecords || 0}
          loading={loading}
          rowUniqueId="id"
          rowClick={false}
        />
      </Box>
    </GlobalWrapper>
  );
}
